import { BarChart3 } from 'lucide-react';
import { useDossiers } from '../../hooks/queries/useDossiers';
import { useBillingDocuments } from '../../hooks/queries/useBillingDocuments';
import { useTimeEntries } from '../../hooks/queries/useTimeEntries';
import { useAuthStore } from '../../store/useAuthStore';
import { canAccessFeature, asCollaboratorRole } from '../../services/auth/permissions';
import { Card } from '../ui/Card';
import { WIDGET_HEADER_FLUSH, WidgetCardHeader } from './WidgetCardHeader';
import { Spinner } from '../ui/Spinner';
import { Sparkline } from './Sparkline';
import {
  computeCabinetKpis,
  computeWeeklySeries,
} from '../../services/dashboard/kpiAggregate';

const eur = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

function fmtHours(minutes: number): string {
  const h = minutes / 60;
  return `${h.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} h`;
}

function Kpi({
  value,
  label,
  hint,
  urgent = false,
}: {
  value: string;
  label: string;
  hint?: string;
  urgent?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <span
        className={`text-[28px] leading-none font-semibold ${urgent ? 'text-cime-urgent' : 'text-cime-primary'}`}
        style={{ fontFamily: 'var(--cime-font-heading)' }}
      >
        {value}
      </span>
      <span className="inline-block w-8 h-0.5 bg-cime-gold mt-2" aria-hidden />
      <span className="text-[11px] font-semibold uppercase tracking-wider text-cime-text-muted mt-1.5">
        {label}
      </span>
      {hint && <span className="text-xs text-neutral-400">{hint}</span>}
    </div>
  );
}

export function CabinetKpisWidget() {
  const rawRole = useAuthStore((s) => s.collaborator?.role);
  const role = asCollaboratorRole(rawRole);

  const dossiersQuery = useDossiers({ include_closed: true, include_archived: true });
  const billingQuery = useBillingDocuments({ doc_type: 'invoice' });
  const timeQuery = useTimeEntries({});

  if (!role || !canAccessFeature(role, 'stats_cabinet_view')) return null;

  const isLoading =
    dossiersQuery.isLoading || billingQuery.isLoading || timeQuery.isLoading;
  const isError =
    dossiersQuery.isError || billingQuery.isError || timeQuery.isError;

  const header = <WidgetCardHeader icon={BarChart3} title="Indicateurs du cabinet" />;

  if (isLoading) {
    return (
      <Card className="h-full" headerClassName={WIDGET_HEADER_FLUSH} header={header}>
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="h-full" headerClassName={WIDGET_HEADER_FLUSH} header={header}>
        <p className="text-sm text-neutral-400 text-center py-4">
          Indicateurs indisponibles
        </p>
      </Card>
    );
  }

  const now = new Date();
  const dossiers = (dossiersQuery.data ?? []).map((d) => ({
    opened_at: d.opened_at,
    archived_at: d.archived_at,
  }));
  const billing = (billingQuery.data ?? []).map((b) => ({
    ht_total: b.ht_total,
    status: b.status,
    issue_date: b.issue_date,
    paid_at: b.paid_at,
  }));
  const time = (timeQuery.data ?? []).map((t) => ({
    duration_minutes: t.duration_minutes,
    is_billable: t.is_billable,
    started_at: t.started_at,
  }));

  const k = computeCabinetKpis({ dossiers, billing, time, now });
  const series = computeWeeklySeries({ billing, time, now });

  return (
    <Card className="h-full" headerClassName={WIDGET_HEADER_FLUSH} header={header}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi value={String(k.dossiersOpened)} label="Dossiers ouverts ce mois" />
        <Kpi value={String(k.dossiersArchived)} label="Dossiers archivés ce mois" />
        <Kpi
          value={eur.format(k.revenueHt)}
          label="CA du mois (HT)"
          hint={`dont ${eur.format(k.revenuePaidHt)} encaissé`}
        />
        <Kpi
          value={fmtHours(k.billableMinutesWeek)}
          label="Heures facturables (semaine)"
        />
        <Kpi
          value={eur.format(k.overdueHt)}
          label="Impayés"
          hint={k.overdueCount > 0 ? `${k.overdueCount} facture${k.overdueCount > 1 ? 's' : ''} en retard` : undefined}
          urgent={k.overdueHt > 0}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-neutral-100 pt-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-neutral-500">CA / semaine (4 sem.)</p>
            <p className="text-sm font-semibold text-neutral-700 tabular-nums">
              {eur.format(series.revenueHt.at(-1) ?? 0)}
            </p>
          </div>
          <Sparkline
            values={series.revenueHt}
            label="CA 4 dernières semaines"
            ariaLabel={`CA 4 dernières semaines : ${series.revenueHt.map((v) => eur.format(v)).join(', ')}`}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-neutral-500">
              Heures facturables / semaine (4 sem.)
            </p>
            <p className="text-sm font-semibold text-neutral-700 tabular-nums">
              {fmtHours(series.billableMinutes.at(-1) ?? 0)}
            </p>
          </div>
          <Sparkline
            values={series.billableMinutes}
            label="Heures facturables 4 dernières semaines"
            ariaLabel={`Heures facturables 4 dernières semaines : ${series.billableMinutes.map((m) => fmtHours(m)).join(', ')}`}
          />
        </div>
      </div>
    </Card>
  );
}
